import { cn } from 'oddle-shop-monorepo.design-system'
import React from 'react'

// Assuming these are the Tailwind mappings or CSS variable names
// These might need adjustment based on the actual theme setup in packages/design-system
const fontWeights = {
  regular: 'font-regular',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

// Updated to map to keys from tailwind.config.js theme.spacing
// The values are the *keys* from your spacing scale (0, 1, 2, 3, 4, 5, 6, 8, 10, etc.)
const marginSizeKeys = {
  none: '0', // maps to spacing.0 (0px)
  extraSmall: '1', // maps to spacing.1 (4px)
  small: '2', // maps to spacing.2 (8px)
  medium: '5', // maps to spacing.5 (20px)
  large: '8', // maps to spacing.8 (32px)
  extraLarge: '12', // maps to spacing.12 (48px)
}

interface BaseTypoProps extends React.HTMLAttributes<HTMLElement> {
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div'
  children?: React.ReactNode
  className?: string

  mobileFontSize?: string // Will become e.g. text-sm, text-base, text-[15px]
  mobileLineHeight?: string // Will become e.g. leading-tight, leading-normal, leading-[20px]
  tabletFontSize?: string // md:text-base
  tabletLineHeight?: string // md:leading-normal
  desktopFontSize?: string // lg:text-lg
  desktopLineHeight?: string // lg:leading-relaxed

  color?: string // e.g., text-red-500, or text-[var(--custom-color)]
  fontWeight?: keyof typeof fontWeights

  margin?: keyof typeof marginSizeKeys
  marginTop?: keyof typeof marginSizeKeys
  marginBottom?: keyof typeof marginSizeKeys
  marginLeft?: keyof typeof marginSizeKeys
  marginRight?: keyof typeof marginSizeKeys

  normalised?: boolean // if true, remove default margins (like p tags have)
  bold?: boolean
  italic?: boolean
  underline?: boolean

  textTransform?: 'uppercase' | 'lowercase' | 'capitalize' | 'normal-case'
  fontFamily?: string // e.g., font-sans, or font-[var(--custom-font)]
  whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line'
  textAlign?: 'left' | 'center' | 'right' | 'justify'

  line?: number // For line clamping
  flexHeight?: boolean // Used with line clamping in the old component
  sectionHeading?: boolean
}

const getMarginClass = (
  value: keyof typeof marginSizeKeys | undefined,
  prefix: string
): string => {
  if (!value) return ''
  const sizeKey = marginSizeKeys[value]
  return `${prefix}-${sizeKey}`
}

export const Text: React.FC<BaseTypoProps> = ({
  as: Component = 'span',
  children,
  className,
  mobileFontSize,
  mobileLineHeight,
  tabletFontSize,
  tabletLineHeight,
  desktopFontSize,
  desktopLineHeight,
  color = 'text-body', // Example: 'text-primary-500' or 'var(--color-text-main)'
  fontWeight: fontWeightKey,
  margin: marginKey,
  marginTop: marginTopKey,
  marginBottom: marginBottomKey,
  marginLeft: marginLeftKey,
  marginRight: marginRightKey,
  normalised,
  bold,
  italic,
  underline,
  textTransform,
  fontFamily, // Example: 'font-body' or 'font-heading' if defined in tailwind.config
  whiteSpace = 'pre-wrap', // default from old component
  textAlign,
  line,
  // flexHeight, // flexHeight was used with height, Tailwind's line-clamp might handle this differently or need custom CSS
  ...props
}) => {
  const classes = cn(
    mobileFontSize, // directly pass Tailwind class e.g. "text-base"
    mobileLineHeight, // "leading-normal"
    tabletFontSize && `md:${tabletFontSize}`, // "md:text-lg"
    tabletLineHeight && `md:${tabletLineHeight}`,
    desktopFontSize && `lg:${desktopFontSize}`, // "lg:text-xl"
    desktopLineHeight && `lg:${desktopLineHeight}`,
    color, // If it's a CSS variable: `text-[var(--color-name)]`, if direct tailwind: `text-blue-500`
    fontWeightKey && fontWeights[fontWeightKey],
    bold && fontWeights.bold, // Apply bold if true and no specific fontWeight is given
    italic && 'italic',
    underline && 'underline',
    textTransform,
    fontFamily, // e.g. "font-sans" or "font-serif" or a custom one
    `whitespace-${whiteSpace}`,
    textAlign && `text-${textAlign}`,
    marginKey && getMarginClass(marginKey, 'm'),
    marginTopKey && getMarginClass(marginTopKey, 'mt'),
    marginBottomKey && getMarginClass(marginBottomKey, 'mb'),
    marginLeftKey && getMarginClass(marginLeftKey, 'ml'),
    marginRightKey && getMarginClass(marginRightKey, 'mr'),
    {
      'overflow-hidden': line !== undefined, // overflow-hidden is often needed with line-clamp
    },
    line !== undefined && `line-clamp-${line}`, // Apply line-clamp utility if line prop is provided
    normalised && (Component === 'p' ? 'm-0' : ''), // Example for 'p', adjust as needed
    className
  )

  // Handling line clamping:
  // The old CSS for line clamping is complex and uses -webkit-box.
  // Tailwind offers a line-clamp plugin: @tailwindcss/line-clamp.
  // If that plugin is installed, classes like `line-clamp-2`, `line-clamp-3` can be used.
  // The code above now conditionally adds `line-clamp-${line}`.
  // Ensure the plugin is installed and configured in your apps/shop/tailwind.config.js.

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  )
}

// Default props from old component (typo.p, typo.h1 etc.) will need to be mapped to Tailwind classes.
// For example: typo.p might have { mobileFontSize: 'text-base', mobileLineHeight: 'leading-relaxed', color: 'text-[var(--color-text-body)]' }

// Example:
// P.defaultProps = {
//   mobileFontSize: 'text-base', // Adjust to actual theme
//   mobileLineHeight: 'leading-normal', // Adjust
//   color: 'text-[var(--color-body-font)]', // Adjust
// };

// TODO:
// 1. Map `defaultProps` from the old `typo` object to Tailwind classes for H1, H2, H3, H4, P, SmallText.
// 2. Implement Header, MenuNameHeader, H1, H2, H3, H4, P, SmallText, LinkText using the new Text component.
// 3. For LinkText, hover states need to be handled (e.g. `hover:text-[var(--color-brand-primary-darker)]`).
// 4. Verify color variables (e.g., `var(--color-brand-primary)`). The guide mentioned `var(--color-brand-500)`.
// 5. The `darken` utility from `tools/style` needs a replacement. The design system is said to provide one.
//    If it's a CSS function, it might be usable in `calc()` or as a pre-processor step.
//    If it's a JS utility, it can't be directly used in Tailwind classes but could be used for inline styles if necessary,
//    or colors could be pre-defined (e.g., brandPrimary, brandPrimaryDarker).
// 6. The `breakpoint` utility is replaced by Tailwind's responsive prefixes (sm, md, lg, xl).
// 7. `customPropTypes` is replaced by Typescript.
// 8. `Div` component import is removed as we use intrinsic elements or other design system components.
// 9. `fontWeights` mapping needs to be verified against the project's Tailwind config.
//10. `marginSizes` need to be mapped to actual Tailwind spacing units (e.g., 's' -> 'space-2' or use arbitrary values like `m-[4px]`).
//    The `getMarginClass` is a temporary solution using arbitrary values.
//11. Line clamping needs @tailwindcss/line-clamp plugin or custom CSS for a clean solution.

export const defaultStyles = {
  h1: {
    mobileFontSize: 'text-3xl',
    tabletFontSize: 'text-3xl',
    desktopFontSize: 'text-3xl',
    mobileLineHeight: 'leading-[1.2]',
    tabletLineHeight: 'leading-[1.2]',
    desktopLineHeight: 'lg:leading-[1.2]',
    fontWeight: 'bold' as keyof typeof fontWeights,
    fontFamily: 'font-headings',
    color: 'text-headings',
  },
  h2: {
    mobileFontSize: 'text-xl',
    tabletFontSize: 'text-xl',
    desktopFontSize: 'text-xl',
    mobileLineHeight: 'leading-[1.25]',
    tabletLineHeight: 'leading-[1.25]',
    desktopLineHeight: 'leading-[1.25]',
    fontWeight: 'bold' as keyof typeof fontWeights,
    fontFamily: 'font-headings',
    color: 'text-headings',
  },
  h3: {
    mobileFontSize: 'text-lg',
    tabletFontSize: 'text-lg',
    desktopFontSize: 'text-lg',
    mobileLineHeight: 'leading-[1.3]',
    tabletLineHeight: 'leading-[1.3]',
    desktopLineHeight: 'leading-[1.3]',
    fontWeight: 'bold' as keyof typeof fontWeights,
    fontFamily: 'font-headings',
    color: 'text-headings',
  },
  h4: {
    mobileFontSize: 'text-lg',
    tabletFontSize: 'text-lg',
    desktopFontSize: 'text-lg',
    mobileLineHeight: 'leading-[1.3]',
    tabletLineHeight: 'leading-[1.3]',
    desktopLineHeight: 'leading-[1.3]',
    fontWeight: 'bold' as keyof typeof fontWeights,
    fontFamily: 'font-headings',
    color: 'text-headings',
  },
  p: {
    mobileFontSize: 'text-base',
    tabletFontSize: 'text-base',
    desktopFontSize: 'text-base',
    mobileLineHeight: 'leading-[1.5]',
    tabletLineHeight: 'leading-[1.5]',
    desktopLineHeight: 'leading-[1.5]',
    fontWeight: 'regular' as keyof typeof fontWeights,
    fontFamily: 'font-body',
    color: 'text-body',
    marginBottom: 'small' as keyof typeof marginSizeKeys,
  },
  span: {
    mobileFontSize: 'text-base',
    tabletFontSize: 'text-base',
    desktopFontSize: 'text-base',
    mobileLineHeight: 'leading-[1.5]',
    tabletLineHeight: 'leading-[1.5]',
    desktopLineHeight: 'leading-[1.5]',
    fontWeight: 'regular' as keyof typeof fontWeights,
    fontFamily: 'font-body',
    color: 'text-body',
    marginBottom: 'none' as keyof typeof marginSizeKeys,
  },
  smallText: {
    mobileFontSize: 'text-sm',
    tabletFontSize: 'text-sm',
    desktopFontSize: 'text-sm',
    mobileLineHeight: 'leading-[1.4]',
    tabletLineHeight: 'leading-[1.4]',
    desktopLineHeight: 'leading-[1.4]',
    fontWeight: 'regular' as keyof typeof fontWeights,
    fontFamily: 'font-body',
    color: 'text-body opacity-80',
  },
}

export const Header: React.FC<BaseTypoProps> = ({
  sectionHeading,
  color,
  fontWeight,
  bold,
  fontFamily,
  ...rest
}) => {
  // Original logic: color: ${props => props.color || (props.sectionHeading ? props.theme.colorSectionHeadingsFont : props.theme.colorHeadingsFont)};
  // Original logic: font-weight: ${props => props.bold ? fontWeights.bold : (props.sectionHeading ? fontWeights.bold : fontWeights.regular)};
  // Original logic: font-family: ${props => props.fontFamily || props.theme.typographyHeadings};

  const defaultHeaderColor = sectionHeading
    ? 'text-section-headings' // Updated
    : 'text-headings' // Updated

  const finalColor = color || defaultHeaderColor

  const defaultWeightKey = sectionHeading ? 'bold' : 'regular'
  const finalWeightKey = bold ? 'bold' : fontWeight || defaultWeightKey
  const finalFontFamily = fontFamily || 'font-headings' // Updated

  return (
    <Text
      color={finalColor}
      fontWeight={finalWeightKey as keyof typeof fontWeights}
      fontFamily={finalFontFamily}
      {...rest}
    />
  )
}

// MenuNameHeader was H1 in terms of style but as 'h1'
export const MenuNameHeader: React.FC<BaseTypoProps> = (props) => (
  <Header as="h1" {...defaultStyles.h1} {...props} />
)

export const H1: React.FC<BaseTypoProps> = (props) => (
  // Original was .attrs(() => ({ as: 'h2' })) but used typo.h1 styles.
  // This seems like a mismatch in the old code. Forcing as='h1' and h1 styles.
  // If it should indeed be an H2 tag with H1 styling, adjust `as` prop.
  <Header as="h2" {...defaultStyles.h1} {...props} />
)

export const H2: React.FC<BaseTypoProps> = (props) => (
  <Header as="h2" {...defaultStyles.h2} {...props} />
)

export const H3: React.FC<BaseTypoProps> = (props) => (
  <Header as="h3" {...defaultStyles.h3} {...props} />
)

export const H4: React.FC<BaseTypoProps> = (props) => (
  <Header as="h4" {...defaultStyles.h4} {...props} />
)

export const P: React.FC<BaseTypoProps> = (props) => (
  <Text as="p" {...defaultStyles.p} {...props} />
)

export const Span: React.FC<BaseTypoProps> = (props) => (
  <Text as="span" {...defaultStyles.span} {...props} />
)

export const SmallText: React.FC<BaseTypoProps> = (props) => (
  // Original was .attrs(() => ({ as: 'p' })). Consider if 'small' or 'span' is more semantic.
  <Text as="p" {...defaultStyles.smallText} {...props} />
)

interface LinkTextProps extends BaseTypoProps {
  heading?: boolean // From old component, influences default color
}

export const LinkText: React.FC<LinkTextProps> = ({
  color,
  heading,
  className,
  ...rest
}) => {
  // Original logic:
  // color: ${props => props.color ? props.color : (props.heading ? props.theme.colorHeadingsFont : colors.brandPrimary)};
  // &:hover {
  //   color: ${props => props.color ? darken(props.color) : (props.heading ? darken(props.theme.colorHeadingsFont) : colors.brandPrimaryDarkest)};
  // }

  // Assuming standard link color and hover effect managed by Tailwind utility classes or CSS variables.
  // Example: text-brand-primary hover:text-brand-primary-darker
  // Or for headings: text-headings hover:text-headings-darker

  // Placeholders - these CSS variables or utility classes need to exist in your Tailwind setup.
  // Assuming link color should align with button text color or a specific link color variable if available.
  // For this example, let's use button themeing as a proxy if no specific link color is found.
  const baseColor = heading
    ? 'text-headings' // Links within headings use heading color
    : 'text-button-text' // Default link color - using themed button text color
  const hoverColor = heading
    ? 'hover:text-headings' // Assuming no specific hover for heading links, or could use opacity
    : 'hover:text-button-text-darker' // Hover state for default links using themed darker variant

  const finalColorClasses = color
    ? '' // If a specific color prop is passed, it might include its own hover state or be static.
    : `${baseColor} ${hoverColor}`

  return (
    <Text
      as="span" // Usually links are spans, unless they are block elements by design
      tabIndex={0}
      className={cn(
        'cursor-pointer underline',
        finalColorClasses,
        color && color,
        className
      )}
      {...rest}
    />
  )
}
